import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { AuthService } from '@services/auth.service';
import { environment } from '@env/environment';
import { INotRespond, ServerNotRespondingService } from '@services/server-not-responding.service';
import { toFormData } from '@utils/toFormData';

import { EXCLUDED_API, EXCLUDED_AUTH_API } from '@constants/exclude_api';
import { LANG_EDGE_HOSTS } from '@utils/CustomTranslateHttpLoader';
import { Router } from '@angular/router';
import { SESSION_MODE } from '@env/session-mode';
import { SessionService } from '@services/session.service';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {

  constructor(
    private auth: AuthService,
    private router: Router,
    private serverNotRespondingService: ServerNotRespondingService,
    private sessionService: SessionService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (
      req.url.startsWith(LANG_EDGE_HOSTS[environment.env]) ||
      req.url.includes('json')
    ) {
      return next.handle(req);
    }

    const isSessionEndpoint = req.url === `${environment.serverAuthUrl}api/session` ||
      req.url === `${environment.serverAuthUrl}api/logout`;
    if (isSessionEndpoint) {
      return next.handle(this.withSessionOptions(req, true));
    }

    const isExcludedAuth = Object.prototype.hasOwnProperty.call(EXCLUDED_AUTH_API, req.url);
    if (isExcludedAuth) {
      return next.handle(req.clone({ withCredentials: SESSION_MODE !== 'legacy' }));
    }

    const isExcludedApi = Object.prototype.hasOwnProperty.call(EXCLUDED_API, req.url);
    if (isExcludedApi) {
      return next.handle(this.withSessionOptions(req.clone({
        body: toFormData({ ...req.body }),
      })));
    }

    const accessData = this.auth.isLoggedIn && SESSION_MODE !== 'cookie'
      ? {
          client_token: this.auth.userData?.key || this.auth.accessToken,
          username: this.auth.userData?.username,
        }
      : {};
    const requestBody = { ...req.body, ...accessData };
    if (SESSION_MODE === 'cookie') delete requestBody.client_token;

    const modifiedReq = this.withSessionOptions(req.clone({
      url: `${environment.serverUrl}${req.url}`,
      body: toFormData(requestBody),
    }));

    return next.handle(modifiedReq).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const body: any = event.body;
          if (body?.code === 'COINDEPO_LISTING') {
            this.router.navigate(['/downtime']);
          }
        }
      }),
      catchError((err: any) => {
        if (err.status === 'error' && err.error_code === 'RELOGIN_REQUIRED') {
          this.auth.logout();
        }
        if (err?.error?.code === 'COINDEPO_LISTING') {
          this.router.navigate(['/downtime']);
        }
        if (err.status == 502 || err.status == 0) {
          this.serverNotResponding({
            code: err.code,
            parent_id: err.parent_id,
          });
        } else {
          this.serverResponding();
        }
        return throwError(err.error);
      })
    );
  }

  private withSessionOptions(
    req: HttpRequest<any>,
    forceCredentials = false
  ): HttpRequest<any> {
    const withCredentials = forceCredentials || SESSION_MODE !== 'legacy';
    let headers = req.headers;
    const mutatingMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
    const csrfToken = this.sessionService.getCsrfToken();
    if (withCredentials && csrfToken && mutatingMethods.includes(req.method)) {
      headers = headers.set('X-CSRF-Token', csrfToken);
    }
    return req.clone({ withCredentials, headers });
  }

  private serverNotResponding(errorData: INotRespond) {
    if(!this.serverNotRespondingService.hasErrorSnapshot()) {
      this.serverNotRespondingService.enableFullscreenError(errorData)
    }
  }

  private serverResponding() {
    if (this.serverNotRespondingService.hasErrorSnapshot()) {
      this.serverNotRespondingService.disableFullscreenError()
    }
  }
}
